import { useState, useEffect, useRef } from "react";

const regions = [
  { name: "朝阳区", users: 312, alarms: 2, pressure: 0.35, pipelines: 156, revenue: 285600, status: "正常" },
  { name: "海淀区", users: 268, alarms: 1, pressure: 0.28, pipelines: 134, revenue: 245800, status: "正常" },
  { name: "西城区", users: 198, alarms: 0, pressure: 0.32, pipelines: 98, revenue: 182400, status: "正常" },
  { name: "东城区", users: 156, alarms: 1, pressure: 0.41, pipelines: 78, revenue: 143200, status: "超压预警" },
  { name: "丰台区", users: 224, alarms: 0, pressure: 0.25, pipelines: 112, revenue: 205600, status: "正常" },
  { name: "石景山", users: 98, alarms: 0, pressure: 0.22, pipelines: 48, revenue: 89800, status: "正常" },
];

const alarmList = [
  { id: "AL001", type: "泄漏告警", location: "海淀区中关村南大街15号", level: "紧急", time: "14:22", status: "未处理" },
  { id: "AL002", type: "压力异常", location: "东城区鼓楼东大街56号", level: "严重", time: "11:35", status: "处理中" },
  { id: "AL003", type: "超压告警", location: "东城区监测点C", level: "严重", time: "09:18", status: "处理中" },
  { id: "AL004", type: "设备离线", location: "朝阳区建国路88号", level: "警告", time: "08:45", status: "已处理" },
  { id: "AL005", type: "余额不足", location: "通州区运河东大街88号", level: "通知", time: "07:30", status: "已处理" },
];

const levelColor: Record<string, string> = {
  "紧急": "bg-red-100 text-red-600",
  "严重": "bg-orange-100 text-orange-600",
  "警告": "bg-amber-100 text-amber-600",
  "通知": "bg-cyan-100 text-cyan-600",
};

const statusColor: Record<string, string> = {
  "未处理": "text-red-500",
  "处理中": "text-amber-500",
  "已处理": "text-green-500",
};

interface Props {
  onClose: () => void;
}

export default function GisRegionPanel({ onClose }: Props) {
  const [tab, setTab] = useState<"region" | "alarm" | "heatmap">("region");
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (tab !== "heatmap") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    // Background
    ctx.fillStyle = "#0f172a";
    ctx.fillRect(0, 0, w, h);

    // Draw simplified Beijing map outline
    ctx.strokeStyle = "rgba(255,255,255,0.15)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(20, 20, w - 40, h - 40, 8);
    ctx.stroke();

    // Heatmap points based on regions
    const heatPoints = [
      { x: w * 0.62, y: h * 0.45, intensity: 0.9, label: "朝阳区" },
      { x: w * 0.38, y: h * 0.32, intensity: 0.8, label: "海淀区" },
      { x: w * 0.42, y: h * 0.52, intensity: 0.7, label: "西城区" },
      { x: w * 0.52, y: h * 0.48, intensity: 0.85, label: "东城区" },
      { x: w * 0.45, y: h * 0.68, intensity: 0.75, label: "丰台区" },
      { x: w * 0.28, y: h * 0.52, intensity: 0.5, label: "石景山" },
    ];

    heatPoints.forEach(pt => {
      const r = 60 * pt.intensity;
      const grad = ctx.createRadialGradient(pt.x, pt.y, 0, pt.x, pt.y, r);
      const alpha = pt.intensity * 0.7;
      if (pt.intensity > 0.8) {
        grad.addColorStop(0, `rgba(239,68,68,${alpha})`);
        grad.addColorStop(0.4, `rgba(249,115,22,${alpha * 0.6})`);
        grad.addColorStop(1, "rgba(249,115,22,0)");
      } else if (pt.intensity > 0.6) {
        grad.addColorStop(0, `rgba(249,115,22,${alpha})`);
        grad.addColorStop(0.4, `rgba(234,179,8,${alpha * 0.6})`);
        grad.addColorStop(1, "rgba(234,179,8,0)");
      } else {
        grad.addColorStop(0, `rgba(34,197,94,${alpha})`);
        grad.addColorStop(0.4, `rgba(34,197,94,${alpha * 0.5})`);
        grad.addColorStop(1, "rgba(34,197,94,0)");
      }
      ctx.beginPath();
      ctx.arc(pt.x, pt.y, r, 0, Math.PI * 2);
      ctx.fillStyle = grad;
      ctx.fill();

      // Label
      ctx.fillStyle = "rgba(255,255,255,0.8)";
      ctx.font = "10px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(pt.label, pt.x, pt.y + 4);
    });

    // Legend
    const legendItems = [
      { color: "rgba(239,68,68,0.8)", label: "高压力区" },
      { color: "rgba(249,115,22,0.8)", label: "中压力区" },
      { color: "rgba(34,197,94,0.8)", label: "低压力区" },
    ];
    legendItems.forEach((item, i) => {
      ctx.fillStyle = item.color;
      ctx.fillRect(w - 90, 30 + i * 20, 12, 10);
      ctx.fillStyle = "rgba(255,255,255,0.6)";
      ctx.font = "9px sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(item.label, w - 74, 39 + i * 20);
    });

    // Title
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.font = "10px sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("管网压力热力分布图", 28, 38);
  }, [tab]);

  return (
    <div className="absolute top-0 right-0 h-full w-80 bg-white border-l border-gray-100 flex flex-col z-[998] overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
        <span className="text-sm font-bold text-gray-800">区域统计面板</span>
        <button onClick={onClose} className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
          <i className="ri-close-line text-gray-400 text-sm"></i>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mx-3 mt-3 flex-shrink-0">
        {[
          { key: "region", label: "区域统计" },
          { key: "alarm", label: "告警列表" },
          { key: "heatmap", label: "热力图" },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key as typeof tab)}
            className={`flex-1 py-1.5 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${tab === t.key ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3">
        {tab === "region" && (
          <div className="space-y-2">
            {/* Summary */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              {[
                { label: "总用户数", value: regions.reduce((s, r) => s + r.users, 0).toLocaleString(), icon: "ri-user-3-line", color: "text-[#E8531A]" },
                { label: "活跃告警", value: regions.reduce((s, r) => s + r.alarms, 0), icon: "ri-alarm-warning-line", color: "text-red-500" },
                { label: "管网里程", value: `${regions.reduce((s, r) => s + r.pipelines, 0)} km`, icon: "ri-route-line", color: "text-cyan-600" },
                { label: "月营收", value: `¥${(regions.reduce((s, r) => s + r.revenue, 0) / 10000).toFixed(0)}万`, icon: "ri-money-cny-circle-line", color: "text-green-600" },
              ].map((s, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-2.5">
                  <div className="flex items-center gap-1.5 mb-1">
                    <i className={`${s.icon} ${s.color} text-xs`}></i>
                    <span className="text-[10px] text-gray-400">{s.label}</span>
                  </div>
                  <div className={`text-sm font-bold ${s.color}`}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* Region list */}
            {regions.map((r, i) => (
              <div key={i} className="bg-white border border-gray-100 rounded-xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-800">{r.name}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${r.status === "正常" ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"}`}>
                    {r.status}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                  {[
                    { label: "用户数", value: `${r.users}户` },
                    { label: "告警数", value: `${r.alarms}个`, color: r.alarms > 0 ? "text-red-500" : "text-gray-700" },
                    { label: "管网压力", value: `${r.pressure} MPa`, color: r.pressure > 0.4 ? "text-red-500" : "text-cyan-600" },
                    { label: "月营收", value: `¥${(r.revenue / 10000).toFixed(1)}万` },
                  ].map((item, j) => (
                    <div key={j} className="flex justify-between text-[10px]">
                      <span className="text-gray-400">{item.label}</span>
                      <span className={`font-medium ${item.color || "text-gray-700"}`}>{item.value}</span>
                    </div>
                  ))}
                </div>
                {/* Pressure bar */}
                <div className="mt-2">
                  <div className="w-full bg-gray-100 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full ${r.pressure > 0.4 ? "bg-red-500" : r.pressure > 0.3 ? "bg-amber-500" : "bg-cyan-500"}`}
                      style={{ width: `${Math.min((r.pressure / 0.5) * 100, 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[9px] text-gray-400 mt-0.5">
                    <span>0 MPa</span>
                    <span>0.5 MPa</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "alarm" && (
          <div className="space-y-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500">共 {alarmList.length} 条告警</span>
              <span className="text-[10px] text-red-500 font-medium">{alarmList.filter(a => a.status === "未处理").length} 条未处理</span>
            </div>
            {alarmList.map((alarm, i) => (
              <div key={i} className={`border rounded-xl p-3 ${alarm.status === "未处理" ? "border-red-100 bg-red-50/30" : "border-gray-100 bg-white"}`}>
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-1.5">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${levelColor[alarm.level]}`}>{alarm.level}</span>
                    <span className="text-xs font-semibold text-gray-800">{alarm.type}</span>
                  </div>
                  <span className={`text-[10px] font-medium ${statusColor[alarm.status]}`}>{alarm.status}</span>
                </div>
                <p className="text-[10px] text-gray-500 mb-1.5 leading-relaxed">{alarm.location}</p>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-gray-400">{alarm.time}</span>
                  {alarm.status === "未处理" && (
                    <button className="text-[10px] bg-[#E8531A] text-white px-2 py-0.5 rounded-full cursor-pointer whitespace-nowrap">
                      立即处理
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "heatmap" && (
          <div>
            <p className="text-[10px] text-gray-400 mb-2">管网压力热力分布（颜色越深压力越高）</p>
            <canvas ref={canvasRef} width={280} height={220} className="w-full rounded-xl" />
            <div className="mt-3 space-y-2">
              <p className="text-xs font-semibold text-gray-700">压力分布统计</p>
              {regions.map((r, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-[10px] text-gray-500 w-14 flex-shrink-0">{r.name}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${r.pressure > 0.4 ? "bg-red-500" : r.pressure > 0.3 ? "bg-amber-500" : "bg-cyan-500"}`}
                      style={{ width: `${(r.pressure / 0.5) * 100}%` }}
                    />
                  </div>
                  <span className={`text-[10px] font-bold w-12 text-right ${r.pressure > 0.4 ? "text-red-500" : "text-gray-600"}`}>
                    {r.pressure} MPa
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
